import { useQueryClient } from '@tanstack/react-query';
import { WatchValues } from '@unistocks/components/form/components/watch-values';
import { Form } from '@unistocks/components/form/form';
import { MultiStepForm } from '@unistocks/components/form/multi-step-form';
import { oneOf } from '@unistocks/components/form/validators';
import { Card } from '@unistocks/components/layout/card';
import { Column, Row } from '@unistocks/components/layout/flex';
import { HeaderIcon } from '@unistocks/components/modal/header-icon';
import { H4, Paragraph } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import {
  DashboardItemAddFormData,
  DashboardItemDescriptor
} from '@unistocks/features/dashboard/dashboard-item-descriptor';
import { getDashboardItemDescriptor } from '@unistocks/features/dashboard/widget-registry';
import { t } from '@unistocks/localization';
import {
  DashboardItemDto,
  DashboardItemSettingsDto,
  DashboardItemType
} from '@unistocks/proxy/axios-client';
import {
  dashboardGetQueryKey,
  useDashboardAddItemMutation
} from '@unistocks/proxy/axios-client/DashboardQuery';
import { Logger } from '@unistocks/shared/services/logger.service';
import { mapEnumToOptions } from '@unistocks/shared/utils/enum-utils';
import { FormPropsBase } from '@unistocks/types/form-props';
import React, { useCallback, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { z } from 'zod';

const dashboardItemTypeOptions = mapEnumToOptions(DashboardItemType, 'DashboardItemType');

const renderItemIcon = (type: DashboardItemType) => {
  const Icon = getDashboardItemDescriptor(type).icon;

  return <HeaderIcon Icon={Icon} color={'$headingFont'} />;
};

const itemTypeField = () => oneOf(dashboardItemTypeOptions.map(({ value }) => value));

export const AddDashboardItemForm: React.FC<FormPropsBase<DashboardItemDto>> = ({
  onSubmit,
  onCancel
}) => {
  const queryClient = useQueryClient();
  const formId = 'add-dashboard-item';
  const [descriptor, setDescriptor] = useState<DashboardItemDescriptor | null>(null);
  const [step, setStep] = useState(0);
  const [ItemFormComponent, LayoutComponent] = descriptor?.settingsSteps ?? [];

  const schema = descriptor
    ? descriptor.schemaFactory().extend({ type: itemTypeField() })
    : z.object({ type: itemTypeField() });
  const { mutateAsync: addItem } = useDashboardAddItemMutation({
    onSuccess: res => {
      toast.success(t('::Dashboard:Success'));
      queryClient.invalidateQueries({ queryKey: dashboardGetQueryKey() });
      onSubmit(res);
    },
    onError: error => {
      Logger.error(error);
    }
  });

  const onFormSubmit = useCallback<SubmitHandler<DashboardItemAddFormData>>(
    data =>
      addItem({
        type: data.type,
        settings: data
      }),
    [addItem]
  );

  return (
    <MultiStepForm
      formId={formId}
      schema={schema}
      step={step}
      onStepChange={setStep}
      onSubmit={onFormSubmit}
      onCancel={onCancel}
    >
      <MultiStepForm.Step<DashboardItemAddFormData> label={t('::Type')}>
        {form => (
          <Form.FieldContainer<DashboardItemAddFormData> name="type">
            <WatchValues<DashboardItemAddFormData> names={['type']}>
              {([selectedType]) => {
                const onItemTypeChange = (type: DashboardItemType) => {
                  form.reset(defaultValues => ({
                    ...defaultValues,
                    title: form.getValues('title')
                  }));

                  const nextDescriptor = getDashboardItemDescriptor(type);
                  nextDescriptor.defaults?.(form.setValue);

                  setDescriptor(nextDescriptor);
                  setStep(1);
                };

                return (
                  <Column gap={'$3'} my={'$3'}>
                    {dashboardItemTypeOptions.map(({ key, value: type }) => (
                      <Card
                        key={type}
                        pressable={'color'}
                        hoverable
                        bordered
                        bg={type === selectedType ? '$color4' : 'transparent'}
                        onPress={() => {
                          form.setValue('type', type);
                          onItemTypeChange(type);
                        }}
                        $platform-web={{ cursor: 'pointer' }}
                      >
                        <Card.Header>
                          <Card.Heading size={'$7'}>{t(key)}</Card.Heading>
                          {renderItemIcon(type)}
                        </Card.Header>
                        <Card.Body>
                          <Paragraph userSelect="none">{t(`${key}:Description`)}</Paragraph>
                        </Card.Body>
                      </Card>
                    ))}
                  </Column>
                );
              }}
            </WatchValues>
          </Form.FieldContainer>
        )}
      </MultiStepForm.Step>
      <MultiStepForm.Step<DashboardItemAddFormData> label={t('::Settings')}>
        <Form.FieldContainer name="settings">
          <WatchValues<DashboardItemAddFormData> names={['type']}>
            {([type]) => (
              <>
                <Row gap={'$2'} alignItems="center" my={'$3'}>
                  {renderItemIcon(type)}
                  <H4 color={'$headingFont'}>{t(`::Enum:DashboardItemType:${type}`)}</H4>
                </Row>

                {ItemFormComponent ? (
                  <ItemFormComponent />
                ) : (
                  <Paragraph textAlign={'center'} pb={'$3'}>
                    {t('::Dashboard:Settings:NoSettings')}
                  </Paragraph>
                )}
              </>
            )}
          </WatchValues>
        </Form.FieldContainer>
      </MultiStepForm.Step>
      <MultiStepForm.Step<DashboardItemSettingsDto> label={t('::Layout')} hidden={!LayoutComponent}>
        <LayoutComponent />
      </MultiStepForm.Step>
    </MultiStepForm>
  );
};
